"use client";

/**
 * Widget Wallet Context
 *
 * Provides widget-specific wallet hooks to SwapTokensForm when mounted.
 * Only used on /widget routes inside an iframe with bridge active.
 *
 * When this provider is NOT mounted (main site), useWidgetWalletContext()
 * returns null, and wrapper hooks fall back to Para hooks.
 */

import { createContext, useContext, ReactNode } from "react";
import {
  useWidgetAccount,
  WidgetAccountResult,
} from "@/hooks/widget/use-widget-account";
import { useWidgetTransactions } from "@/hooks/widget/use-widget-transactions";
import { VersionedTransaction, Transaction } from "@solana/web3.js";

/**
 * Context value shape - provides account data and transaction function
 * with the same interfaces as useParaAccount and useParaTransactions.
 */
export interface WidgetWalletContextValue {
  /** Account data from useWidgetAccount (matches useParaAccount interface) */
  accountData: WidgetAccountResult;
  /** Transaction sender from useWidgetTransactions (matches useParaTransactions.onSendTransaction) */
  onSendTransaction: (
    transaction: VersionedTransaction | Transaction
  ) => Promise<string>;
}

/**
 * Context - null when provider is not mounted (main site).
 * This allows wrapper hooks to detect widget mode and fall back to Para hooks.
 */
const WidgetWalletContext = createContext<WidgetWalletContextValue | null>(
  null
);

/**
 * Provider component - mount ONLY on /widget routes.
 *
 * Calls widget hooks internally and provides their values to children.
 * SwapTokensForm and other components access these via useWidgetWalletContext().
 */
export function WidgetWalletProvider({ children }: { children: ReactNode }) {
  const accountData = useWidgetAccount();
  const { onSendTransaction } = useWidgetTransactions();

  return (
    <WidgetWalletContext.Provider value={{ accountData, onSendTransaction }}>
      {children}
    </WidgetWalletContext.Provider>
  );
}

/**
 * Hook to access widget wallet context.
 *
 * Returns null when WidgetWalletProvider is not mounted (main site).
 * Wrapper hooks (useSwapAccount, useSwapTransactions) check this and
 * fall back to Para hooks when null.
 */
export function useWidgetWalletContext(): WidgetWalletContextValue | null {
  return useContext(WidgetWalletContext);
}
